import { api } from '@/services/api';
import { unifiedServices } from '@/services/unifiedService';
import { isSupabaseMode } from '@/config/dataSource';
import { API_ROUTES } from '@/config/api';
import { UserRole, UserStatus } from '@/shared/enums';

export interface User {
  id: string; // UUID do Supabase
  email: string;
  username: string; // Nome de usuário único para login
  first_name: string;
  last_name: string;
  role: UserRole;
  status: UserStatus;
  tenant_id: string | null; // ID da empresa
  supervisorId?: number | null;
  tenant?: {
    id: string;
    nome: string;
  };
  created_at: string;
  updated_at: string;
}

export interface CreateUserDTO {
  email: string;
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  status: UserStatus;
  tenant_id: string | null;
  supervisorId?: number | null;
}

export interface UpdateUserDTO {
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: UserRole;
  status?: UserStatus;
  tenant_id?: string | null;
  supervisorId?: number | null;
}

class SuperadminService {
  async getUsers(): Promise<User[]> {
    if (isSupabaseMode()) {
      return await unifiedServices.superadmin.getUsers();
    }
    const { data } = await api.get(API_ROUTES.SUPERADMIN.USERS.BASE);
    return data;
  }

  async createUser(userData: CreateUserDTO): Promise<User> {
    if (isSupabaseMode()) {
      return await unifiedServices.superadmin.createUser(userData);
    }
    const { data } = await api.post(API_ROUTES.SUPERADMIN.USERS.BASE, userData);
    return data;
  }

  async updateUser(userId: any, userData: UpdateUserDTO): Promise<User> {
    if (isSupabaseMode()) {
      const updatedUserData: any = { ...userData };
      if (updatedUserData.tenant_id !== undefined) {
        // Garantir que tenant_id seja número ou null para o banco (bigint)
        updatedUserData.tenant_id = updatedUserData.tenant_id ? Number(updatedUserData.tenant_id) : null;
      }
      return await unifiedServices.superadmin.updateUser(userId, updatedUserData);
    }
    const { data } = await api.put(API_ROUTES.SUPERADMIN.USERS.DETAILS(userId), userData);
    return data;
  }

  async checkUserDependencies(userId: any): Promise<{
    hasClientes: boolean;
    hasContratos: boolean;
    clientesCount: number;
    contratosCount: number;
    totalDependencies: number;
  }> {
    if (isSupabaseMode()) {
      return await unifiedServices.superadmin.checkUserDependencies(userId);
    }
    const { data } = await api.get(`${API_ROUTES.SUPERADMIN.USERS.BASE}/${userId}/dependencies`);
    return data;
  }

  async deleteUser(userId: any, forceUnlink: boolean = false): Promise<void> {
    if (isSupabaseMode()) {
      return await unifiedServices.superadmin.deleteUser(userId, forceUnlink);
    }
    await api.delete(API_ROUTES.SUPERADMIN.USERS.DETAILS(userId));
  }

  async resetPassword(userId: any, newPassword: string): Promise<void> {
    if (isSupabaseMode()) {
      return await unifiedServices.superadmin.resetPassword(userId, newPassword);
    }
    const { data } = await api.post(`${API_ROUTES.SUPERADMIN.USERS.BASE}/${userId}/reset-password`, {
      new_password: newPassword
    });
    return data;
  }

  async getDashboardStats(): Promise<{ users: number; tenants: number; clientes: number; contratos: number }> {
    if (isSupabaseMode()) {
      return await unifiedServices.superadmin.getDashboardStats();
    }
    const { data } = await api.get(API_ROUTES.SUPERADMIN.STATS);
    return data;
  }
}

export const superadminService = new SuperadminService();